import * as React from "react";
import * as Select from "@radix-ui/react-select";
import { ChevronDown, Check } from "lucide-react";
import { cn } from "@/lib/utils";

interface SelectOption {
  label: string;
  value: string | number;
}

interface SelectInputProps extends React.ComponentPropsWithoutRef<typeof Select.Root> {
  label?: string;
  options: SelectOption[];
  placeholder?: string;
  className?: string;
  error?: string;
  id?: string;
  onChange?: (value: string) => void;
  value?: string;
}

const SelectInput = React.forwardRef<React.ElementRef<typeof Select.Trigger>, SelectInputProps>(
  ({ className, label, options, placeholder = "", error, id, onChange, value, ...props }, ref) => (
    <div>
      {label && (
        <label
          htmlFor={id}
          className="mb-1.5 cursor-pointer text-[15px] font-normal text-system-disabled"
        >
          {label}
        </label>
      )}
      <Select.Root value={value} onValueChange={onChange} {...props}>
        <Select.Trigger
          ref={ref}
          id={id}
          className={cn(
            "flex h-[2.625rem] w-full items-center justify-between rounded-lg border bg-system-graphite px-4 py-3",
            "text-base text-system-disabled focus:outline-none focus:ring-0",
            error
              ? "border-system-error focus:border-system-error"
              : "border-[#61646C66] focus:border-system-primary",
            className
          )}
        >
          <Select.Value placeholder={placeholder} className="placeholder:text-system-titanium" />
          <Select.Icon>
            <ChevronDown className="h-5 w-5" />
          </Select.Icon>
        </Select.Trigger>

        <Select.Portal>
          <Select.Content
            className="w-[--radix-select-trigger-width] overflow-hidden rounded-lg bg-system-graphite p-1"
            position="popper"
            sideOffset={8}
          >
            <Select.Viewport className="max-h-[300px] overflow-y-auto">
              {options.map(option => (
                <Select.Item
                  key={option.value}
                  value={String(option.value)}
                  className="flex cursor-pointer items-center justify-between rounded-md px-4 py-2 text-system-disabled outline-none data-[highlighted]:bg-system-primary data-[highlighted]:text-white"
                >
                  <Select.ItemText>{option.label}</Select.ItemText>
                  <Select.ItemIndicator>
                    <Check className="h-4 w-4" />
                  </Select.ItemIndicator>
                </Select.Item>
              ))}
            </Select.Viewport>
          </Select.Content>
        </Select.Portal>
      </Select.Root>
      {error && <p className="mt-1 text-[13px] text-system-error">{error}</p>}
    </div>
  )
);

SelectInput.displayName = "SelectInput";

export default SelectInput;
